import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import UserService from "../services/user.service";
import clientService from "../services/client.service";
import mobileDeviceService from "../services/mobileDevice.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const MobileDeviceManager = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [responseCode, setResponseCode] = useState(400);
  const [mobileDevices, setMobileDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null });
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    setLoading(true);
    UserService.getClientList().then(
      (response) => {
        setContent(response.data);
        if (response.data instanceof Array && response.data.length > 0) {
          const isAdmin = currentUser && currentUser.roles && currentUser.roles.includes("ROLE_ADMIN");
          if (!isAdmin) {
            setSelectedOption(response.data[0].oid);
          }
        }
        setLoading(false);
        setResponseCode(200);
      },
      (error) => {
        const _content =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        console.log(error);
        setContent(_content);
        setResponseCode(error.response?.status || 500);
        setLoading(false);
      }
    );
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !selectedOption) return;

    const fetchMobileDevices = async () => {
      setLoading(true);
      try {
        const response = await mobileDeviceService.getMobileDevices(
          selectedOption,
          currentPage,
          pageSize
        );
        setMobileDevices(response.data.content);
        setPagination({
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          currentPage: response.data.number,
          pageSize: response.data.size,
          isFirst: response.data.first,
          isLast: response.data.last
        });
        setResponseCode(200);
      } catch (error) {
        const _content =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        console.log(error);
        setContent(_content);
        setResponseCode(error.response?.status || 500);
      } finally {
        setLoading(false);
      }
    };

    fetchMobileDevices();
  }, [selectedOption, currentPage, pageSize, currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const handleSelection = (event) => {
    setSelectedOption(event.target.value);
    setCurrentPage(0);
    setSelectedDevices([]);
  };

  const handleSearchClients = async () => {
    if (!searchTerm || searchTerm.trim() === "") {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please enter a search term" });
      return;
    }
    if (searchTerm.trim().length < 3) {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Search term must be at least 3 characters long" });
      return;
    }

    setSearchLoading(true);
    try {
      const response = await clientService.searchClients(searchTerm);
      const results = response.data;
      if (!results || results.length === 0) {
        toast.info("No clients found matching the search term");
      } else if (results.length === 1) {
        // Automatically select if only 1 result
        setSelectedOption(results[0].oid);
        setSearchTerm(results[0].clientName);
        setCurrentPage(0);
        setSelectedDevices([]);
        toast.success(`Automatically selected client: ${results[0].clientName}`);
      } else {
        // Show modal for multiple results
        setSearchResults(results);
        setShowSearchModal(true);
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "An error occurred while searching for clients" });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchClients();
    }
  };

  const handleSelectSearchResult = (clientId, clientName) => {
    setSelectedOption(clientId);
    setSearchTerm(clientName);
    setCurrentPage(0);
    setSelectedDevices([]);
    setShowSearchModal(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedDevices([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setSelectedDevices([]);
  };

  const handleSelectDevice = (deviceId) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === mobileDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(mobileDevices.map(d => d.id));
    }
  };

  const handleBatchDelete = () => {
    if (selectedDevices.length === 0) return;

    setConfirmDialog({
      show: true,
      message: `Are you sure you want to delete ${selectedDevices.length} mobile device(s)?`,
      onConfirm: async () => {
        setDeleteLoading(true);
    try {
      await mobileDeviceService.batchDeleteMobileDevices(selectedOption, selectedDevices);
      toast.success("Mobile devices deleted successfully");
      setSelectedDevices([]);
      // Refresh the list
      const response = await mobileDeviceService.getMobileDevices(
        selectedOption,
        currentPage,
        pageSize
      );
      setMobileDevices(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to delete mobile devices";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Batch delete failed:", error);
        } finally {
          setDeleteLoading(false);
          setConfirmDialog({ show: false, message: '', onConfirm: null });
        }
      }
    });
  };

  const handleDeleteSingle = (deviceId) => {
    setConfirmDialog({
      show: true,
      message: "Are you sure you want to delete this mobile device?",
      onConfirm: async () => {
        setDeleteLoading(true);
    try {
      await mobileDeviceService.batchDeleteMobileDevices(selectedOption, [deviceId]);
      toast.success("Mobile device deleted successfully");
      // Refresh the list
      const response = await mobileDeviceService.getMobileDevices(
        selectedOption,
        currentPage,
        pageSize
      );
      setMobileDevices(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to delete mobile device";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Delete failed:", error);
        } finally {
          setDeleteLoading(false);
          setConfirmDialog({ show: false, message: '', onConfirm: null });
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    return status === 1 ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-secondary">Inactive</span>
    );
  };

  return (
    <div className="container-fluid py-4 px-4 px-lg-5 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <h2 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Mobile Device Management</h2>
        {loading && <span className="spinner-border text-primary" role="status"></span>}
      </div>

      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <div className="card-body p-4">
          <div className="row align-items-end">
            <div className="col-md-8">
              <label className="form-label fw-bold text-muted mb-2">Select Client</label>
              {currentUser && currentUser.roles && currentUser.roles.includes("ROLE_ADMIN") ? (
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-light border-0"
                    placeholder="Search Client by Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
                  <button
                    className="btn-premium px-4 border-0"
                    type="button"
                    onClick={handleSearchClients}
                    disabled={searchLoading}
                  >
                    {searchLoading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : <><i className="bi bi-search me-2"></i>Search</>}
                  </button>
            </div>
          ) : (
            <div className="dropdown">
                <select
                  value={selectedOption}
                  className="form-select form-select-lg bg-light border-0"
                  onChange={handleSelection}
                >
                  {!content && (
                    <option value="" disabled>
                      No client registration found
                    </option>
                  )}
                  {responseCode === 200 && content && content.map((client, index) => (
                    <option key={client.oid} value={client.oid}>
                      {client.clientName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            </div>
            <div className="col-md-4">
              <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                <small className="text-muted text-uppercase fw-bold mb-1">Client ID</small>
                <span className="fw-medium text-primary">{selectedOption || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {responseCode >= 400 && content && (
        <div className="mb-4">
          <div className="alert alert-danger rounded-4 border-0 shadow-sm" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {typeof content === "string"
              ? content
              : (content.hasOwnProperty("message") ? content.message : "Something went wrong")
            }
          </div>
        </div>
      )}

      {/* Batch Actions */}
      {selectedDevices.length > 0 && (
        <div className="mb-4">
          <div className="alert alert-info border-0 shadow-sm d-flex justify-content-between align-items-center rounded-4" style={{ backgroundColor: 'rgba(79, 70, 229, 0.1)', color: 'var(--text-main)' }}>
            <span className="fw-medium"><i className="bi bi-info-circle-fill me-2" style={{ color: '#4f46e5' }}></i>{selectedDevices.length} mobile device(s) selected</span>
            <button
              onClick={handleBatchDelete}
              disabled={deleteLoading}
              className="btn btn-danger rounded-pill px-4"
            >
              <i className="bi bi-trash-fill me-2"></i>
              {deleteLoading ? 'Deleting...' : 'Delete Selected'}
            </button>
          </div>
        </div>
      )}

      {/* Page Size Selector & Mobile Device Table inside one unified Card */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold text-secondary">Mobile Device List</h5>
          <div className="d-flex align-items-center">
            <label className="form-label mb-0 me-2 small fw-bold text-muted">Items per page:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="form-select form-select-sm bg-light border-0"
              style={{ width: '80px', cursor: 'pointer' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
              <tr>
                <th scope="col">
                  <input
                    type="checkbox"
                    checked={selectedDevices.length === mobileDevices.length && mobileDevices.length > 0}
                    onChange={handleSelectAll}
                    className="form-check-input"
                  />
                </th>
                <th scope="col">ID</th>
                <th scope="col">Device ID</th>
                <th scope="col">Description</th>
                <th scope="col">User Name</th>
                <th scope="col">App Name</th>
                <th scope="col">Status</th>
                <th scope="col">Personal Folder</th>
                <th scope="col">Created</th>
                <th scope="col">Updated</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mobileDevices && mobileDevices.length > 0 ? (
                mobileDevices.map((device) => (
                  <tr key={device.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.id)}
                        onChange={() => handleSelectDevice(device.id)}
                        className="form-check-input"
                      />
                    </td>
                    <td>{device.id}</td>
                    <td>{device.deviceId}</td>
                    <td>{device.deviceDesc}</td>
                    <td>{device.userName}</td>
                    <td>{device.appName || 'N/A'}</td>
                    <td>{getStatusBadge(device.status)}</td>
                    <td>{device.personalFolderName}</td>
                    <td>
                      {device.createdAt
                        ? new Date(device.createdAt).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    <td>
                      {device.updatedAt
                        ? new Date(device.updatedAt).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    <td>
                      <button
                        onClick={() => handleDeleteSingle(device.id)}
                        disabled={deleteLoading}
                        className="btn btn-sm btn-outline-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center">
                    {loading ? 'Loading mobile devices...' : 'No mobile devices found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="container mt-3">
          <nav aria-label="Mobile device pagination">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={pagination.isFirst}
                  className="btn btn-outline-primary"
                >
                  Previous
                </button>
              </div>

              <div className="text-center">
                <span className="badge bg-secondary">
                  Page {currentPage + 1} of {pagination.totalPages}
                  ({pagination.totalElements} total mobile devices)
                </span>
              </div>

              <div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={pagination.isLast}
                  className="btn btn-outline-primary"
                >
                  Next
                </button>
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmDialog.show && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header border-bottom-0 py-3 bg-danger text-white">
                <h5 className="modal-title fw-bold"><i className="bi bi-exclamation-triangle-fill me-2"></i>Confirm Action</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })}></button>
              </div>
              <div className="modal-body p-4 text-center">
                <h5 className="mb-0 text-secondary">{confirmDialog.message}</h5>
              </div>
              <div className="modal-footer bg-light border-top-0 d-flex justify-content-center">
                <button type="button" className="btn btn-secondary rounded-pill px-4 me-2" onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })} disabled={deleteLoading}>Cancel</button>
                <button type="button" className="btn btn-danger rounded-pill px-4" disabled={deleteLoading} onClick={() => {
                  if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                }}>
                  {deleteLoading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Results Modal */}
      {showSearchModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                <h5 className="modal-title fw-bold"><i className="bi bi-search me-2"></i>Select Client</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowSearchModal(false)}
                ></button>
              </div>
              <div className="modal-body p-0" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((client) => (
                        <tr key={client.oid}>
                          <td>{client.clientName}</td>
                          <td>{client.email || "-"}</td>
                          <td>{client.phone || "-"}</td>
                          <td>
                            <button
                              className="btn-premium px-3 py-1 btn-sm rounded-pill"
                              onClick={() => handleSelectSearchResult(client.oid, client.clientName)}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light border-top-0">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4"
                  onClick={() => setShowSearchModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MobileDeviceManager;

